import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { cn } from '@ideoz/ui';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Check,
  CornerLeftUp,
  Eye,
  EyeOff,
  Shapes,
  Sparkles,
  Image as ImageIcon,
  MoreHorizontal,
  Table2,
  Type,
} from 'lucide-react';
import {
  askSmartPrompt,
  hotEditStore,
  showHint,
  type HotEditBridgeApi,
  type HotEditSelection,
} from '../../stores/hotEdit';
import { tokenLabel } from '../../preview-bridge/designTokens';
import { tokenPromptFor, type StyleChange } from './smartPrompts';
import { ImagePicker } from './ImagePicker';
import { IconPicker } from './IconPicker';
import { GLASS, RISE, SURFACE, TIP } from './surface';
import { assetPathForDataUri } from '../../preview-bridge/imageReplace';
import { workbenchStore } from '../../stores/workbench';
import { isStyleSection, planPanel, repeatChipHint, rowNoun } from './panelModel';
import {
  ALIGN_PRESETS,
  SIZE_PRESETS,
  WEIGHT_PRESETS,
  activePreset,
  designSwatches,
  elementLabel,
  type Preset,
  type Swatch,
} from './inspectorModel';

const TOOLBAR_HEIGHT = 40;
const GAP = 14;
/** Closer than this to the top of the preview, the bar goes under the element instead. */
const FLIP_BELOW_AT = 56;

/**
 * The toolbar over the selected element (Prototype Editor UX plan, step 2): the four or five
 * things most changes need, where the founder is already looking. Everything else is behind
 * More, which opens the panel. Apply carries the count of pending changes.
 */
export function EditToolbar({
  api,
  onMore,
  frame,
}: {
  api: HotEditBridgeApi | null;
  onMore: () => void;
  /** The preview iframe's box, so the toolbar sits over the element and stays inside it. */
  frame: { top: number; left: number; width: number; height: number } | null;
}): React.ReactElement | null {
  const mode = useStore(hotEditStore.mode);
  const selection = useStore(hotEditStore.selection);
  const rect = useStore(hotEditStore.selectionRect);
  const [open, setOpen] = useState<'size' | 'weight' | 'colour' | 'align' | 'picture' | 'icon' | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);
  // The bar rises once, when the founder first selects; moving between elements is instant.
  const [entering, setEntering] = useState(true);
  const hasSelection = !!selection;

  useEffect(() => setOpen(null), [selection?.elementId]);
  // Measured, not guessed: the bar's width decides how far left it can sit before it would run
  // off the preview's right edge (an element at the end of the page put it out of view).
  useEffect(() => {
    const width = barRef.current?.offsetWidth ?? 0;
    if (width && width !== barWidth) setBarWidth(width);
  });
  useEffect(() => {
    if (!hasSelection) {
      setEntering(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setEntering(false), 260);
    return () => window.clearTimeout(timer);
  }, [hasSelection]);

  if (!mode || !selection || !api || !rect || !frame) return null;

  const plan = planPanel(selection);
  if (plan.blockedReason) return null;

  // Above the ring, left-aligned to it; under it when there is no room above. Either way it
  // stays inside the preview: an element at the very end of the page used to push the toolbar
  // past the right edge, where the founder could not reach half of it.
  const below = rect.top < FLIP_BELOW_AT;
  const rawTop = below
    ? frame.top + rect.top + rect.height + GAP
    : frame.top + rect.top - TOOLBAR_HEIGHT - GAP;
  const top = Math.min(
    Math.max(rawTop, frame.top + 8),
    frame.top + frame.height - TOOLBAR_HEIGHT - 8,
  );
  const rightEdge = frame.left + frame.width - (barWidth || 0) - 8;
  const left = Math.max(frame.left + 8, Math.min(frame.left + rect.left, Math.max(frame.left + 8, rightEdge)));
  // A menu opens downward unless the bar is low in the preview, where it would be cut off.
  const menusOpenUp = top > frame.top + frame.height - 260;
  const classes = selection.className;
  const swatches = designSwatches(selection.themeTokens);
  const showText = plan.sections.includes('typeStyle');
  const showRecords = plan.sections.includes('records') && plan.recordId;
  const showHide = plan.sections.includes('visible');
  const isPicture = selection.tagName.toLowerCase() === 'img';
  // A Lucide icon renders an <svg>; the component that drew it is the name to swap.
  const isIcon = selection.tagName.toLowerCase() === 'svg';

  // A Lucide icon is drawn in `currentColor`, so its colour is a text colour. `bg-*` painted a
  // square behind it and left the icon itself untouched.
  const paintsInk = showText || isIcon;
  const cssProperty = paintsInk ? 'color' : 'backgroundColor';
  const classPrefix = paintsInk ? 'text' : 'bg';

  /**
   * A style on one of a list's rows goes on the row they are all drawn from, so it shows on
   * every one of them. The editor used to ask which the founder meant - and then wrote it wide
   * whatever they answered, because a single row has nowhere to keep a style of its own. So it
   * says what will happen instead of asking a question with one real answer, once.
   */
  const hintRepeat = (_change: StyleChange) => {
    const row = selection.listRow;
    if (!row || row.count < 2) return;
    const tag = selection.tagName.toLowerCase();
    const where = tag === 'td' || tag === 'th' ? 'cell in this column' : rowNoun(selection).replace(/s$/, '');
    showHint(
      'list-style-goes-wide',
      `This goes on every ${where} - all ${row.count} of them - because they are drawn from one design.`,
    );
  };

  const pick = (preset: Preset) => {
    for (const [prop, value] of Object.entries(preset.css)) api.applyInstantCss(selection.elementId, prop, value);
    api.bumpMergedFromTailwind('__hot_tw_util', preset.cls);
    api.queuePersistTypographyLayout();
    setOpen(null);
    hintRepeat('look');
  };
  const colour = (s: Swatch) => {
    api.applyInstantCss(selection.elementId, cssProperty, s.css);
    api.bumpMergedFromTailwind('__hot_tw_util', `${classPrefix}-${s.token}`);
    api.queuePersistTypographyLayout();
    setOpen(null);
    hintRepeat('colour');
  };

  /** The colour on this element alone, as an exact value rather than a design system name. */
  const keepColourHere = (hex: string) => {
    api.bumpMergedFromTailwind(cssProperty, hex);
    api.queuePersistTypographyLayout();
    hintRepeat('colour');
  };

  /**
   * A colour the founder mixed themselves. If the element takes this colour from the design
   * system today, that is a decision about the whole prototype, so the editor asks before
   * making it - showing the answer live either way.
   */
  const customColour = (hex: string) => {
    api.applyInstantCss(selection.elementId, cssProperty, hex);
    const prompt = tokenPromptFor({ selection, hex, property: classPrefix, files: fileContents() });
    if (!prompt || prompt.kind !== 'token') {
      keepColourHere(hex);
      return;
    }
    askSmartPrompt({
      title: prompt.title,
      body: prompt.body,
      broad: {
        label: prompt.everywhere,
        run: () => {
          // The element goes back to its `bg-primary`; what changes is what Primary is.
          api.applyInstantCss(selection.elementId, cssProperty, '');
          api.previewThemeToken(prompt.token, prompt.hsl);
          api.queueThemeToken(prompt.token, prompt.hsl, `${tokenLabel(prompt.token)} colour`);
        },
      },
      narrow: { label: prompt.onlyHere, run: () => keepColourHere(hex) },
    });
  };

  return (
    <div
      ref={barRef}
      className={cn(GLASS, entering && SURFACE, 'absolute z-30 flex h-10 items-center gap-0.5 rounded-[13px] px-1.5')}
      style={{ top, left }}
      role="toolbar"
      aria-label={`Edit ${elementLabel(selection)}`}
      data-testid="hot-edit-toolbar"
      onMouseDown={(e) => {
        // The caret stays in the preview when the founder reaches for a control - but this also
        // took the click away from the icon search, which could never be typed in.
        if ((e.target as HTMLElement).closest('input:not([type="color"]), textarea')) return;
        e.preventDefault();
      }}
    >
      <span className="max-w-[140px] truncate pl-1.5 pr-2 text-xs font-semibold">{plan.title}</span>
      {plan.repeatChip && plan.sections.some(isStyleSection) ? (
        <span
          className="mr-1 rounded-full px-[7px] py-1 text-[10.5px] font-semibold leading-none"
          style={{ background: 'var(--editor-accent-soft)', color: 'var(--editor-accent)' }}
          title={repeatChipHint(selection.listRow?.count ?? 0)}
        >
          All {selection.listRow?.count}
        </span>
      ) : null}
      <Divider />

      {showText ? (
        <>
          <Menu
            label="Size"
            icon={<Type size={14} />}
            isOpen={open === 'size'}
            onToggle={() => setOpen(open === 'size' ? null : 'size')}
            active={activePreset(classes, SIZE_PRESETS)?.label}
          >
            {SIZE_PRESETS.map((p) => (
              <MenuItem key={p.cls} label={p.label} on={activePreset(classes, SIZE_PRESETS)?.cls === p.cls} onPick={() => pick(p)} />
            ))}
          </Menu>
          <Menu
            label="Weight"
            icon={<span className="font-bold">B</span>}
            isOpen={open === 'weight'}
            onToggle={() => setOpen(open === 'weight' ? null : 'weight')}
          >
            {WEIGHT_PRESETS.map((p) => (
              <MenuItem key={p.cls} label={p.label} on={activePreset(classes, WEIGHT_PRESETS)?.cls === p.cls} onPick={() => pick(p)} />
            ))}
          </Menu>
          <Menu
            label="Alignment"
            icon={<AlignIcon classes={classes} />}
            isOpen={open === 'align'}
            onToggle={() => setOpen(open === 'align' ? null : 'align')}
          >
            {ALIGN_PRESETS.map((p) => (
              <MenuItem key={p.cls} label={p.label} on={activePreset(classes, ALIGN_PRESETS)?.cls === p.cls} onPick={() => pick(p)} />
            ))}
          </Menu>
        </>
      ) : null}

      {swatches.length > 0 ? (
        <Menu
          label={paintsInk ? 'Colour' : 'Background'}
          icon={<span className="h-3.5 w-3.5 rounded-full" style={{ background: swatches[0]!.css, boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.7)' }} />}
          isOpen={open === 'colour'}
          onToggle={() => setOpen(open === 'colour' ? null : 'colour')}
        >
          <div className="flex flex-wrap gap-1.5 p-1" role="group" aria-label="Colours">
            {swatches.map((s) => (
              <button
                key={s.token}
                type="button"
                title={s.label}
                aria-label={s.label}
                className="h-6 w-6 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
                style={{ background: s.css }}
                onClick={() => colour(s)}
              />
            ))}
            <label
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-dashed border-border text-[13px] leading-none text-muted-foreground hover:bg-muted"
              title="A colour of your own"
            >
              +
              <input
                type="color"
                className="sr-only"
                aria-label="A colour of your own"
                data-testid="hot-edit-custom-colour"
                onChange={(e) => {
                  setOpen(null);
                  customColour(e.target.value);
                }}
              />
            </label>
          </div>
        </Menu>
      ) : null}

      {isIcon ? (
        <Menu
          label="Change icon"
          icon={<Shapes size={14} />}
          isOpen={open === 'icon'}
          onToggle={() => setOpen(open === 'icon' ? null : 'icon')}
          openUp={menusOpenUp}
        >
          <IconPicker
            current={selection.ownerComponentName}
            onPick={(name, markup) => {
              api.swapIcon(name, markup);
              setOpen(null);
            }}
          />
        </Menu>
      ) : null}

      {isPicture ? (
        <Menu
          label="Replace picture"
          icon={<ImageIcon size={14} />}
          isOpen={open === 'picture'}
          onToggle={() => setOpen(open === 'picture' ? null : 'picture')}
        >
          <ImagePicker
            current={assetPathForDataUri(workbenchStore.assets.get(), selection.imageSrc)}
            onPick={(picked) => {
              api.replaceImage(picked);
              setOpen(null);
              hintRepeat('look');
            }}
          />
        </Menu>
      ) : null}

      {showRecords || showHide ? <Divider /> : null}
      {showRecords ? (
        <Button
          label="Edit list data"
          onClick={() => hotEditStore.dataGrid.set({ recordId: plan.recordId! })}
          icon={<Table2 size={14} />}
        />
      ) : null}
      {showHide ? (
        <Button
          label={selection.isHidden ? 'Unhide' : 'Hide'}
          icon={selection.isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
          onClick={() => api.toggleHidden(selection.isHidden !== true)}
        />
      ) : null}

      <Divider />
      <Button
        label="Select what this is inside"
        icon={<CornerLeftUp size={14} />}
        onClick={() => api.selectParent()}
      />
      <Button label="All options" icon={<MoreHorizontal size={14} />} onClick={onMore} />
      <Button
        label="Ask Ideoz about this element"
        icon={<Sparkles size={14} />}
        onClick={() => api.askIdeoz()}
      />

    </div>
  );
}

/** The prototype's source, flat, for the rules that read it. */
function fileContents(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, file] of Object.entries(workbenchStore.files.get())) {
    if (file?.content != null) out[path] = file.content;
  }
  return out;
}

function AlignIcon({ classes }: { classes: string }): React.ReactElement {
  const active = activePreset(classes, ALIGN_PRESETS)?.cls;
  if (active === 'text-center') return <AlignCenter size={14} />;
  if (active === 'text-right') return <AlignRight size={14} />;
  return <AlignLeft size={14} />;
}

function Divider(): React.ReactElement {
  return <span className="mx-1 h-[18px] w-px bg-current opacity-[0.14]" aria-hidden />;
}

function Button({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      data-tip={label}
      data-ideoz-icon-button=""
      className={cn(TIP, 'grid h-7 w-7 place-items-center rounded-lg opacity-85 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10')}
      onClick={onClick}
    >
      {icon}
    </button>
  );
}

function Menu({
  label,
  icon,
  isOpen,
  onToggle,
  active,
  openUp,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  active?: string;
  /** Open above the button: the bar is low enough that a menu below would be cut off. */
  openUp?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onToggle();
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        data-tip={label}
        className={cn(
          TIP,
          'flex h-7 min-w-7 items-center justify-center gap-1 rounded-lg px-1.5 opacity-85 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10',
          isOpen && 'bg-black/10 opacity-100 dark:bg-white/15',
        )}
        onClick={onToggle}
      >
        {icon}
        {active ? <span className="text-[11px] font-medium">{active}</span> : null}
      </button>
      {isOpen ? (
        <div
          className={cn(
            GLASS,
            RISE,
            'absolute left-0 z-40 min-w-[140px] rounded-[14px] p-1',
            openUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      className={cn(
        'flex h-[30px] w-full items-center justify-between rounded-lg px-2 text-left text-xs hover:bg-black/5 dark:hover:bg-white/10',
        on && 'font-semibold',
      )}
      onClick={onPick}
    >
      {label}
      {on ? <Check size={12} /> : null}
    </button>
  );
}
